import React, { useEffect, useState, useMemo } from 'react';
import prayerTimesService from '../../services/prayerTimesService';
import { 
  getRealisticSkyColors, 
  getLocationFromZip, 
  createElegantGradient, 
  isDarkBackground,
  getContrastColor,
  getGradientColorAtPosition,
  getTextStyle,
  getColorLuminance
} from '../../services/skyColorService';
import { getDynamicSolarColor, getTextColorForBackground } from '../../services/solarColorSystem';

/**
 * Prayer Times Widget Component
 * Displays Shia prayer times for the center location
 */
const PrayerTimesWidget = ({ config, theme, prayerTimes: initialTimes, loading: externalLoading, error: externalError }) => {
  const [prayerTimes, setPrayerTimes] = useState(initialTimes);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locationTimezone, setLocationTimezone] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  // Update viewport width on resize
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update location-based time every second for accurate display
  useEffect(() => {
    const updateTime = () => {
      // If we have timezone from prayer times, use it
      if (locationTimezone) {
        const locationTime = prayerTimesService.getCurrentTimeForLocation(locationTimezone);
        setCurrentTime(locationTime);
      } else {
        // Default: use local system time until we get prayer times
        setCurrentTime(new Date());
      }
    };
    
    updateTime(); // Initial update
    const timer = setInterval(updateTime, 30000); // Update every 30 seconds

    return () => clearInterval(timer);
  }, [locationTimezone]);

  // Fetch prayer times if not provided
  useEffect(() => {
    // Auto-cleanup any stale cache with year 1441 on mount
    const cleanupStaleCache = () => {
      let cleanedCount = 0;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('prayerTimes_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            // Clear if year is 1441 (old stale data from 2019)
            if (data.hijri?.year === '1441' || data.hijri?.year === 1441) {
              localStorage.removeItem(key);
              cleanedCount++;
              console.log(`Auto-cleaned stale cache with year 1441: ${key}`);
            }
            // Also check if old cache without sunset field or version
            else if (!data.sunset || !data.cacheVersion) {
              localStorage.removeItem(key);
              cleanedCount++;
              console.log(`Auto-cleaned old cache without required fields: ${key}`);
            }
          } catch (e) {
            // If can't parse, remove it
            localStorage.removeItem(key);
            cleanedCount++;
            console.log(`Auto-cleaned corrupted cache: ${key}`);
          }
        }
      }
      if (cleanedCount > 0) {
        console.log(`[PrayerTimesWidget] Auto-cleaned ${cleanedCount} stale cache entries`);
      }
    };
    
    // Run cleanup on mount
    cleanupStaleCache();
    
    // Try center's ZIP code first, then prayer_settings ZIP code
    const zipCode = config?.zip_code || config?.prayer_settings?.zip_code;
    if (!initialTimes && zipCode) {
      fetchPrayerTimes();
    } else if (initialTimes) {
      setPrayerTimes(initialTimes);
      // Set timezone from prayer times data
      if (initialTimes.timezone) {
        setLocationTimezone(initialTimes.timezone);
      }
      setLoading(false);
    } else if (externalLoading !== undefined) {
      setLoading(externalLoading);
    }
  }, [initialTimes, config, externalLoading]);

  // Update next prayer when times change or time updates
  useEffect(() => {
    if (prayerTimes) {
      const next = prayerTimesService.getNextPrayer(prayerTimes, locationTimezone);
      setNextPrayer(next);
      // Debug logging - commented out to reduce console spam
      // console.log('Next Prayer Detection:', {
      //   next,
      //   prayerTimes,
      //   currentTime: currentTime.toLocaleTimeString(),
      //   timezone: locationTimezone
      // });
    }
  }, [prayerTimes, currentTime, locationTimezone]);

  // Determine which prayer period we're currently in
  const getCurrentPrayerIndex = () => {
    if (!prayerTimes || !currentTime) return -1;
    
    const prayers = [
      { name: 'Fajr', time: prayerTimes.fajr },
      { name: 'Sunrise', time: prayerTimes.sunrise },
      { name: 'Dhurain', time: prayerTimes.dhuhr },
      { name: 'Maghrebain', time: prayerTimes.maghrib }
    ];
    
    // Get current time in location timezone
    const tz = locationTimezone || 'America/New_York';
    const nowInLocation = new Date(currentTime.toLocaleString("en-US", { timeZone: tz }));
    const currentMinutes = nowInLocation.getHours() * 60 + nowInLocation.getMinutes();
    
    // Find the last prayer that has passed - that's our current prayer period
    let currentPrayerIdx = -1;
    
    for (let i = prayers.length - 1; i >= 0; i--) {
      const [hours, minutes] = prayers[i].time.split(':').map(Number);
      const prayerMinutes = hours * 60 + minutes;
      
      if (currentMinutes >= prayerMinutes) {
        currentPrayerIdx = i;
        break;
      }
    }
    
    // If no prayer has started yet today, we're still in yesterday's Maghreb period
    // (This handles the case from midnight to Fajr)
    if (currentPrayerIdx === -1) {
      // Find Maghrebain index
      return prayers.findIndex(p => p.name === 'Maghrebain');
    }
    
    return currentPrayerIdx;
  };

  const fetchPrayerTimes = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use center's ZIP code first, then prayer_settings ZIP code
      const zipCode = config?.zip_code || config?.prayer_settings?.zip_code;
      const times = await prayerTimesService.fetchShiaPrayerTimes(zipCode);
      setPrayerTimes(times);
      // Set timezone from fetched prayer times or use center's timezone
      const timezone = times.timezone || config?.timezone;
      if (timezone) {
        setLocationTimezone(timezone);
      }
    } catch (err) {
      setError('Failed to load prayer times');
    } finally {
      setLoading(false);
    }
  };

  // Get realistic sky colors based on sun position
  const colors = useMemo(() => {
    const zipCode = config?.zip_code || config?.prayer_settings?.zip_code || '07039';
    const location = getLocationFromZip(zipCode);
    
    // SunCalc automatically handles sun position based on the current time and coordinates
    // No need to convert time - just pass the current Date object
    const skyColors = getRealisticSkyColors(currentTime, location.lat, location.lng);
    
    return skyColors;
  }, [currentTime?.getMinutes(), config?.prayer_settings?.zip_code, locationTimezone]); // Update every minute

  // Format time for display with responsive sizing
  const formatTime = (time24) => {
    if (!time24) return '';
    const converted = prayerTimesService.convertTo12Hour(time24);
    
    // For very small screens, use shorter format
    if (viewportWidth < 360) {
      return converted.replace(' AM', 'a').replace(' PM', 'p');
    }
    return converted;
  };

  // Format current date and time
  const formatCurrentDateTime = () => {
    // Use location timezone if available, otherwise default to America/New_York
    const tz = locationTimezone || 'America/New_York';
    
    const dateOptions = { 
      month: 'short', // Changed to short for "Sept" format
      day: 'numeric', 
      year: 'numeric',
      timeZone: tz
    };
    const timeOptions = { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: tz
    };
    
    const dateStr = currentTime.toLocaleDateString('en-US', dateOptions);
    const timeStr = currentTime.toLocaleTimeString('en-US', timeOptions);
    return { dateStr, timeStr };
  };

  // Debug functions removed - issue fixed with DD-MM-YYYY date format

  // Check if a prayer time has passed
  const isPrayerPassed = (time24) => {
    if (!time24 || !currentTime) return false;
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      
      // Get current time in location timezone
      const tz = locationTimezone || 'America/New_York';
      const nowInLocation = new Date(currentTime.toLocaleString("en-US", { timeZone: tz }));
      
      // Create prayer time in the same day
      const prayerTime = new Date(nowInLocation);
      prayerTime.setHours(hours, minutes, 0, 0);
      
      return nowInLocation > prayerTime;
    } catch (error) {
      return false;
    }
  };

  // Responsive prayer names - with 4 boxes we have more space
  const getPrayerName = (name) => {
    if (viewportWidth < 360) {
      // Ultra compact - but with more space now
      const abbreviations = {
        'Fajr': 'Fajr',
        'Sunrise': 'Sunrise',
        'Dhurain': 'Dhurain',
        'Maghrebain': 'Maghrib'
      };
      return abbreviations[name] || name;
    } else if (viewportWidth < 400) {
      // Compact - can show full names with 4 boxes
      const shortNames = {
        'Fajr': 'Fajr',
        'Sunrise': 'Sunrise',
        'Dhurain': 'Dhurain',
        'Maghrebain': 'Maghrebain'
      };
      return shortNames[name] || name;
    }
    return name;
  };

  // Get Hijri date for Islamic day (changes at Maghrib, not midnight)
  // Priority: Living calendar (Google Sheet) > API + hijri_adjustment fallback
  const getHijriDateForIslamicDay = () => {
    if (!prayerTimes?.maghrib) return prayerTimes?.hijri || null;

    const now = new Date();
    const tz = locationTimezone || 'America/New_York';
    const localTime = new Date(now.toLocaleString("en-US", { timeZone: tz }));

    // Parse Maghrib time to determine if Islamic day has turned
    const [maghribHour, maghribMin] = prayerTimes.maghrib.split(':').map(Number);
    const maghribDate = new Date(localTime);
    maghribDate.setHours(maghribHour, maghribMin, 0, 0);
    const maghribPassed = localTime >= maghribDate;

    // Try living calendar from center's Google Sheet (islamicOccasionsService)
    // This data is loaded in RichHomePage and passed via config context
    if (window.__islamicCalendarData && window.__islamicCalendarData.length > 0) {
      try {
        const { gregorianToHijri, formatHijriDate, HIJRI_MONTH_NAMES } = require('../../services/islamicOccasionsService');
        // If after Maghrib, compute for tomorrow (next Islamic day)
        const targetDate = maghribPassed
          ? new Date(localTime.getFullYear(), localTime.getMonth(), localTime.getDate() + 1)
          : localTime;
        const hijri = gregorianToHijri(targetDate, window.__islamicCalendarData);
        if (hijri) {
          console.log('[PrayerTimesWidget] Hijri from living calendar:', hijri);
          return {
            date: `${hijri.day}-${hijri.month}-${hijri.year}`,
            day: String(hijri.day),
            month: hijri.monthName,
            year: String(hijri.year),
          };
        }
      } catch (e) {
        // Fall through to API-based calculation
        console.warn('[PrayerTimesWidget] Living calendar lookup failed, using API fallback');
      }
    }

    // Fallback: API-based calculation with hijri_adjustment
    if (!prayerTimes?.hijri) return null;

    const baseAdjustment = config?.hijri_adjustment || 0;
    const totalAdjustment = baseAdjustment + (maghribPassed ? 1 : 0);

    console.log('[PrayerTimesWidget] Hijri from API + adjustment:', {
      currentTime: localTime.toLocaleTimeString(),
      maghribTime: prayerTimes.maghrib,
      maghribPassed,
      baseAdjustment,
      totalAdjustment,
    });

    if (totalAdjustment !== 0) {
      return prayerTimesService.adjustHijriDate(prayerTimes.hijri, totalAdjustment);
    }

    return prayerTimes.hijri;
  };

  // Get theme color
  const themeColor = theme?.colors?.primary || config?.primaryColor || '#001f3f';
  
  // Helper function to get lighter shade of a color
  const getLighterShade = (color) => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Make it lighter
    const lighterR = Math.min(255, r + 40);
    const lighterG = Math.min(255, g + 40);
    const lighterB = Math.min(255, b + 40);
    
    // Convert back to hex
    return `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;
  };
  
  // Helper function to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color) return false;
    
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance (WCAG formula)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if light (threshold at 0.5 for good contrast)
    return luminance > 0.5;
  };
  
  // Get dynamic solar-based accent color
  const solarColorInfo = useMemo(() => {
    const zipCode = config?.prayer_settings?.zip_code || '07039';
    const location = getLocationFromZip(zipCode);
    
    // Get dynamic color based on actual sunrise/sunset times and prayer times
    const colorInfo = getDynamicSolarColor(
      currentTime, 
      location.lat, 
      location.lng,
      {
        fajrTime: prayerTimes?.fajr,     // Pass Fajr time for pre-dawn calculations
        maghribTime: prayerTimes?.maghrib, // Pass Maghrib time for evening calculations
        fallbackColor: themeColor // Use theme color as fallback
      }
    );
    
    return colorInfo;
  }, [currentTime?.getMinutes(), config?.prayer_settings?.zip_code, themeColor, prayerTimes?.fajr, prayerTimes?.maghrib]);
  
  const accentColor = solarColorInfo.color;
  
  // Determine if accent color is light and needs dark text
  const isAccentLight = useMemo(() => isLightColor(accentColor), [accentColor]);
  
  // Dynamic text colors based on accent brightness
  const accentTextColor = isAccentLight ? '#1f2937' : '#ffffff';  // Dark gray or white
  const accentTextColorSecondary = isAccentLight ? '#374151' : '#e5e7eb';  // Secondary shades
  
  // Create darker version of theme color for better contrast
  const darkThemeColor = useMemo(() => {
    // Convert hex to RGB and darken by 40%
    const hex = themeColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const darkenFactor = 0.6; // 60% of original brightness
    const darkR = Math.floor(r * darkenFactor);
    const darkG = Math.floor(g * darkenFactor);
    const darkB = Math.floor(b * darkenFactor);
    return `rgb(${darkR}, ${darkG}, ${darkB})`;
  }, [themeColor]);
  
  // Simple gradient with darker edges for better contrast
  const headerGradient = `linear-gradient(135deg, 
    ${darkThemeColor} 0%, 
    ${themeColor} 15%,
    ${accentColor}99 35%,
    ${accentColor}99 65%,
    ${themeColor} 85%,
    ${darkThemeColor} 100%
  )`;
  
  // Get sun position on arc (0-100%)
  const getSunPosition = () => {
    if (!prayerTimes) return 50;
    
    const now = currentTime;
    const [fajrH, fajrM] = prayerTimes.fajr.split(':').map(Number);
    const [maghribH, maghribM] = prayerTimes.maghrib.split(':').map(Number);
    
    const fajrMinutes = fajrH * 60 + fajrM;
    const maghribMinutes = maghribH * 60 + maghribM;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    
    // If before sunrise or after sunset, return null (show moon)
    if (nowMinutes < fajrMinutes || nowMinutes > maghribMinutes) {
      return null;
    }
    
    // Calculate position on arc (0% = sunrise, 100% = sunset)
    const dayLength = maghribMinutes - fajrMinutes;
    const elapsed = nowMinutes - fajrMinutes;
    return Math.min(100, Math.max(0, (elapsed / dayLength) * 100));
  };

  // Get moon position for night time
  const getMoonPosition = () => {
    if (!prayerTimes) return 50;
    
    const now = currentTime;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    
    const [maghribH, maghribM] = prayerTimes.maghrib.split(':').map(Number);
    const [fajrH, fajrM] = prayerTimes.fajr.split(':').map(Number);
    
    const maghribMinutes = maghribH * 60 + maghribM;
    const fajrMinutes = fajrH * 60 + fajrM;
    
    // Calculate night duration and position
    if (nowMinutes > maghribMinutes) {
      // Evening to midnight
      const elapsed = nowMinutes - maghribMinutes;
      const nightFirstHalf = (24 * 60) - maghribMinutes;
      const totalNight = nightFirstHalf + fajrMinutes;
      return (elapsed / totalNight) * 100;
    } else if (nowMinutes < fajrMinutes) {
      // Midnight to dawn
      const nightFirstHalf = (24 * 60) - maghribMinutes;
      const elapsed = nightFirstHalf + nowMinutes;
      const totalNight = nightFirstHalf + fajrMinutes;
      return (elapsed / totalNight) * 100;
    }
    
    return null; // Day time
  };

  // Calculate sun and moon positions
  const sunPosition = getSunPosition();
  const moonPosition = getMoonPosition();

  // Get dynamic text colors based on the solar color luminance
  const textColors = useMemo(() => {
    // Check if accent color is bright
    const hex = accentColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Force dark colors for bright backgrounds
    if (luminance > 0.6) {
      return {
        primary: '#111827',    // Very dark gray
        secondary: '#374151',  // Dark gray
        shadow: '0 1px 3px rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.3)'  // White shadow for contrast
      };
    }
    
    // Default to auto-calculated colors for darker backgrounds
    return getTextColorForBackground(accentColor);
  }, [accentColor]);
  
  // Widget styles with clean, elegant design
  const styles = {
    widget: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '0',
      marginBottom: '52px',
      marginLeft: viewportWidth < 768 ? '8px' : '0', // Reduce padding on mobile
      marginRight: viewportWidth < 768 ? '8px' : '0',
      boxShadow: `0 4px 24px rgba(0,0,0,0.12), 0 1px 3px ${themeColor}22`,
      border: `1px solid ${themeColor}20`,
      position: 'relative',
      overflow: 'hidden'  // Clip gradients to boundaries
    },
    content: {
      position: 'relative',
      padding: '0'
    },
    header: {
      background: darkThemeColor,
      borderRadius: '16px 16px 0 0',
      position: 'relative',
      overflow: 'hidden'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'stretch',
      height: viewportWidth < 400 ? '80px' : '90px',
      position: 'relative'
    },
    dateBox: {
      width: '33.333%',
      background: darkThemeColor,
      color: '#ffffff',
      padding: viewportWidth < 400 ? '12px' : '16px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      gap: '4px',
      position: 'relative',
      zIndex: 1
    },
    centerSection: {
      width: '33.333%',
      background: darkThemeColor,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      padding: '12px 8px'
    },
    // Sun/Moon Arc Styles
    arcContainer: {
      width: viewportWidth < 400 ? '100px' : '110px',
      height: viewportWidth < 400 ? '50px' : '55px',
      position: 'relative',
      marginBottom: '8px',
      marginTop: '4px'
    },
    arcPath: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      borderTop: sunPosition !== null 
        ? `3px solid ${accentColor}66`
        : '3px solid #B0C4DE',
      borderLeft: sunPosition !== null 
        ? `3px solid ${accentColor}66`
        : '3px solid #B0C4DE',
      borderRight: sunPosition !== null 
        ? `3px solid ${accentColor}66`
        : '3px solid #B0C4DE',
      borderBottom: 'none',
      borderRadius: '110px 110px 0 0',
      background: sunPosition !== null 
        ? `linear-gradient(90deg, 
            ${accentColor}22 0%, 
            ${accentColor}33 50%, 
            ${accentColor}22 100%)`
        : `linear-gradient(90deg,
            #B0C4DE33 0%,
            #E6E6FA44 50%,
            #B0C4DE33 100%)`,
      opacity: sunPosition !== null ? 0.5 : 0.7
    },
    celestialBody: {
      position: 'absolute',
      width: viewportWidth < 400 ? '18px' : '20px',
      height: viewportWidth < 400 ? '18px' : '20px',
      borderRadius: '50%',
      background: sunPosition !== null ? accentColor : '#B0C4DE',
      boxShadow: sunPosition !== null 
        ? `0 0 20px ${accentColor}, 0 0 40px ${accentColor}66`
        : '0 0 20px #B0C4DE, 0 0 40px #4169E166',
      top: '-9px',
      transform: 'translateX(-50%)',
      transition: 'left 60s linear',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2
    },
    celestialIcon: {
      fontSize: viewportWidth < 400 ? '12px' : '14px',
      lineHeight: 1
    },
    arcTimeLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginTop: '2px'
    },
    timeLabel: {
      fontSize: '9px',
      color: sunPosition !== null ? `${accentColor}99` : '#B0C4DE',
      fontWeight: '500'
    },
    timeRemaining: {
      fontSize: viewportWidth < 400 ? '12px' : '13px',
      color: '#ffffff',
      fontWeight: '700',
      opacity: 0.95
    },
    timeBox: {
      width: '33.333%',
      background: darkThemeColor,
      color: '#ffffff',
      padding: viewportWidth < 400 ? '12px' : '16px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      gap: '4px',
      position: 'relative',
      zIndex: 1
    },
    dateText: {
      fontSize: viewportWidth < 400 ? '15px' : '17px',
      fontWeight: '700',
      letterSpacing: '0.3px',
      color: '#ffffff',
      marginBottom: '2px',
      lineHeight: 1
    },
    hijriDate: {
      fontSize: viewportWidth < 400 ? '12px' : '13px',
      fontWeight: '400',
      color: '#ffffff',
      opacity: 0.85,
      letterSpacing: '0.2px'
    },
    locationText: {
      fontSize: viewportWidth < 400 ? '10px' : '11px',
      opacity: 0.7,
      fontWeight: '400',
      color: '#ffffff',
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    timeDisplay: {
      fontSize: viewportWidth < 400 ? '24px' : '28px',
      fontWeight: '700',
      letterSpacing: '-0.5px',
      color: '#ffffff',
      lineHeight: 1,
      textAlign: 'center'
    },
    prayerSection: {
      position: 'relative',
      background: '#ffffff',
      paddingTop: '15px',  // Space for NEXT label
      borderRadius: '0 0 16px 16px'
    },
    nextLabelWrapper: {
      position: 'absolute',
      top: '5px',
      transform: 'translateX(-50%)',
      zIndex: 20,  // Above everything
      pointerEvents: 'none'  // Don't block clicks
    },
    nextLabelOutside: {
      display: 'inline-block',
      fontSize: '10px',
      background: darkThemeColor,
      color: '#ffffff',
      padding: '3px 12px',
      borderRadius: '12px',
      fontWeight: '800',
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      border: '2px solid white',
      whiteSpace: 'nowrap'
    },
    currentLabelWrapper: {
      position: 'absolute',
      top: '5px',
      transform: 'translateX(-50%)',
      zIndex: 19,  // Just below NEXT
      pointerEvents: 'none'  // Don't block clicks
    },
    currentLabelOutside: {
      display: 'inline-block',
      fontSize: '10px',
      background: `${accentColor}dd`,  // Semi-transparent accent color
      color: isAccentLight ? '#1f2937' : '#ffffff',  // Dynamic text color
      padding: '3px 12px',
      borderRadius: '12px',
      fontWeight: isAccentLight ? '800' : '700',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
      border: isAccentLight ? '2px solid #374151' : '2px solid white',
      whiteSpace: 'nowrap'
    },
    prayerTimesContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: viewportWidth < 360 ? '3px' : '4px',
      padding: viewportWidth < 400 ? '10px' : '12px',
      paddingTop: '15px',  // Space below NEXT label
      background: '#ffffff',
      position: 'relative'
    },
    prayerTimeItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: viewportWidth < 360 ? '6px 4px' : '8px 6px',
      borderRadius: '8px',
      background: '#f8f9fa',
      position: 'relative',
      transition: 'all 0.3s ease',
      border: '1px solid #e9ecef',
      flex: '1',
      minWidth: '0'
    },
    prayerTimePassed: {
      opacity: 0.4,
      background: '#f1f3f5'
    },
    prayerTimeCurrent: {
      background: `linear-gradient(135deg, ${accentColor}22, ${accentColor}33)`,
      boxShadow: `0 2px 8px ${accentColor}44`,
      border: `2px solid ${accentColor}66`
    },
    prayerTimeNext: {
      background: `linear-gradient(135deg, ${themeColor}, ${accentColor})`,
      boxShadow: `0 4px 12px ${themeColor}33`,
      transform: 'scale(1.02)',
      border: 'none'
    },
    prayerName: {
      fontSize: viewportWidth < 360 ? '10px' : viewportWidth < 400 ? '11px' : '12px',  // Increased back with more space
      fontWeight: '500',
      marginBottom: '2px',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
      letterSpacing: '0.4px',  // Slightly more spacing with extra room
      opacity: 0.8,  // Slightly increased for theme color
      color: themeColor  // Use theme color for consistency
    },
    prayerTime: {
      fontSize: viewportWidth < 360 ? '12px' : viewportWidth < 400 ? '13px' : '14px',
      fontWeight: '700',  // Bolder for better readability
      whiteSpace: 'nowrap',
      color: themeColor  // Use theme color for consistency
    },
    prayerTimeNextText: {
      color: '#ffffff'
    },
    loading: {
      textAlign: 'center',
      padding: '60px',
      color: '#6c757d',
      background: '#ffffff'
    },
    error: {
      textAlign: 'center',
      padding: '20px',
      color: '#dc2626',
      backgroundColor: 'rgba(254, 242, 242, 0.9)',
      backdropFilter: 'blur(8px)',
      borderRadius: '8px',
      margin: '16px'
    }
  };

  // No heavy animations needed for performance
  
  // Loading state
  if (loading) {
    return (
      <div style={styles.widget}>
        <div style={styles.content}>
          <div style={styles.loading}>Loading prayer times...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || externalError) {
    return (
      <div style={styles.widget}>
        <div style={styles.content}>
          <div style={styles.error}>{error || externalError}</div>
        </div>
      </div>
    );
  }

  // No prayer times available
  if (!prayerTimes) {
    return null;
  }

  const prayers = [
    { name: 'Fajr', time: prayerTimes.fajr },
    { name: 'Sunrise', time: prayerTimes.sunrise },
    { name: 'Dhurain', time: prayerTimes.dhuhr },
    { name: 'Maghrebain', time: prayerTimes.maghrib }
  ].filter(prayer => prayer.time);

  const { dateStr, timeStr } = formatCurrentDateTime();
  const hijriDate = getHijriDateForIslamicDay();

  // Detect today's Islamic occasion mood for widget styling
  const todayMood = (() => {
    if (!window.__islamicCalendarData || !hijriDate) return null;
    try {
      const { gregorianToHijri, getOccasionsForDate } = require('../../services/islamicOccasionsService');
      const hijri = gregorianToHijri(new Date(), window.__islamicCalendarData);
      if (!hijri) return null;
      // We need the master occasions — check if they're cached on window
      const occasions = window.__islamicOccasionsData;
      if (!occasions) return null;
      const todayOccasions = getOccasionsForDate(hijri.month, hijri.day, occasions);
      if (!todayOccasions.length) return null;
      const primary = todayOccasions[0];
      return { type: primary.type, name: primary.nameEn, nameAr: primary.nameAr };
    } catch { return null; }
  })();

  // Get current prayer index
  const currentPrayerIndex = getCurrentPrayerIndex();

  // Find which prayer is next for positioning the label
  const nextPrayerIndex = prayers.findIndex((prayer) => {
    return nextPrayer && 
      ((nextPrayer.name === 'Dhuhr' && prayer.name === 'Dhurain') ||
       (nextPrayer.name === 'Maghrib' && prayer.name === 'Maghrebain') ||
       (nextPrayer.name === prayer.name));
  });

  return (
    <div style={styles.widget}>
      <div style={styles.content}>
        {/* Header with three equal sections */}
        <div style={{
          ...styles.header,
          ...(todayMood?.type === 'mourning' ? { background: '#111111' } : {}),
        }}>
          <div style={styles.headerContent}>
            {/* Left 33%: Date */}
            <div style={styles.dateBox}>
              <div style={styles.dateText}>{dateStr}</div>
              {hijriDate && (
                <div style={styles.hijriDate}>
                  {hijriDate.day} {typeof hijriDate.month === 'string' ? hijriDate.month : hijriDate.month?.en} {hijriDate.year}
                </div>
              )}
            </div>
            
            {/* Middle 33%: Sun/Moon Arc Visualization */}
            <div style={styles.centerSection}>
              {/* Sun/Moon Arc Container */}
              <div style={styles.arcContainer}>
                <div style={styles.arcPath}></div>
                
                {/* Sun or Moon Dot */}
                <div style={{
                  ...styles.celestialBody,
                  left: sunPosition !== null 
                    ? `${sunPosition}%` 
                    : moonPosition !== null 
                      ? `${moonPosition}%` 
                      : '50%'
                }}>
                  <span style={styles.celestialIcon}>
                    {sunPosition !== null ? '☀️' : '🌙'}
                  </span>
                </div>
              </div>
              
              {/* Arc Time Labels */}
              <div style={styles.arcTimeLabels}>
                <span style={styles.timeLabel}>
                  {sunPosition !== null ? prayerTimes?.fajr : prayerTimes?.maghrib}
                </span>
                <span style={styles.timeLabel}>
                  {sunPosition !== null ? prayerTimes?.maghrib : prayerTimes?.fajr}
                </span>
              </div>
            </div>
            
            {/* Right 33%: Time — AM/PM on separate line */}
            <div style={styles.timeBox}>
              <div style={styles.timeDisplay}>
                {timeStr.replace(/\s?(AM|PM)/i, '')}
              </div>
              <div style={{
                fontSize: '11px',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.6)',
                marginTop: '2px',
                textAlign: 'right',
              }}>
                {timeStr.match(/AM|PM/i)?.[0] || ''}
              </div>
            </div>
          </div>
        </div>

        {/* Islamic occasion banner — between header and prayer times */}
        {todayMood && (
          <div style={{
            padding: '8px 16px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.3px',
            background: todayMood.type === 'mourning'
              ? 'linear-gradient(135deg, #111 0%, #1a1a1a 100%)'
              : todayMood.type === 'joyous'
              ? `linear-gradient(135deg, ${themeColor}15 0%, #fefce8 100%)`
              : `linear-gradient(135deg, ${themeColor}10 0%, #eff6ff 100%)`,
            color: todayMood.type === 'mourning' ? '#ccc' : themeColor,
            borderBottom: todayMood.type === 'mourning'
              ? '1px solid #333'
              : `1px solid ${themeColor}20`,
          }}>
            {todayMood.type === 'mourning' ? '🏴' : todayMood.type === 'joyous' ? '🎉' : '✨'}{' '}
            {todayMood.name}
          </div>
        )}

        {/* Prayer Times Section with CURRENT and NEXT labels positioned outside overflow */}
        <div style={styles.prayerSection}>
          {/* CURRENT Label - for the active prayer period */}
          {currentPrayerIndex >= 0 && currentPrayerIndex !== nextPrayerIndex && (
            <div style={{
              ...styles.currentLabelWrapper,
              left: `${(currentPrayerIndex * (100 / prayers.length)) + (50 / prayers.length)}%`
            }}>
              <span style={styles.currentLabelOutside}>CURRENT</span>
            </div>
          )}
          
          {/* NEXT Label - positioned absolutely but outside overflow container */}
          {nextPrayerIndex >= 0 && (
            <div style={{
              ...styles.nextLabelWrapper,
              left: `${(nextPrayerIndex * (100 / prayers.length)) + (50 / prayers.length)}%`
            }}>
              <span style={styles.nextLabelOutside}>NEXT</span>
            </div>
          )}
          
          {/* Prayer Times Container */}
          <div style={styles.prayerTimesContainer}>
            {prayers.map((prayer, index) => {
              const isPassed = isPrayerPassed(prayer.time);
              const isCurrent = index === currentPrayerIndex;
              const isNext = nextPrayer && 
                ((nextPrayer.name === 'Dhuhr' && prayer.name === 'Dhurain') ||
                 (nextPrayer.name === 'Maghrib' && prayer.name === 'Maghrebain') ||
                 (nextPrayer.name === prayer.name));
              
              // Determine which style to apply
              let prayerStyle = styles.prayerTimeItem;
              if (isNext) {
                prayerStyle = { ...prayerStyle, ...styles.prayerTimeNext };
              } else if (isCurrent) {
                prayerStyle = { ...prayerStyle, ...styles.prayerTimeCurrent };
              } else if (isPassed) {
                prayerStyle = { ...prayerStyle, ...styles.prayerTimePassed };
              }
              
              return (
                <div 
                  key={prayer.name}
                  style={prayerStyle}
                >
                  <div style={{
                    ...styles.prayerName,
                    ...(isNext ? {
                      color: isAccentLight ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                      textShadow: isAccentLight ? 'none' : '0 1px 3px rgba(0,0,0,0.3)',
                      fontWeight: isAccentLight ? '700' : '600',
                      opacity: 1  // Full opacity for next prayer
                    } : isCurrent ? {
                      fontWeight: '600',
                      opacity: 1  // Full opacity for current prayer
                    } : {})
                  }}>
                    {getPrayerName(prayer.name)}
                  </div>
                  <div style={{
                    ...styles.prayerTime,
                    ...(isNext ? {
                      color: isAccentLight ? '#1f2937' : '#ffffff',
                      fontWeight: '800'  // Extra bold for next prayer
                    } : isCurrent ? {
                      fontWeight: '700'  // Bold for current prayer
                    } : {})
                  }}>
                    {formatTime(prayer.time)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrayerTimesWidget;